import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Eye, EyeOff, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { copyText } from "@/lib/copy";

export const SyncKeyPanel = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  const [token, setToken] = useState("");
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null);
  const [views, setViews] = useState([]);
  const load = useCallback(() => {
    api("get", "/sync/key").then(setD).catch((e) => toast.error(errMsg(e)));
    api("get", "/sync/key/views").then(setViews).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!shown) { return undefined; }
    const t = setTimeout(() => setShown((s) => (s && s.left > 1 ? { ...s, left: s.left - 1 } : null)), 1000);
    return () => clearTimeout(t);
  }, [shown]);
  const reveal = () => run(async () => { const r = await api("post", "/sync/key/reveal"); setShown({ token: r.token, left: r.hide_after_seconds }); api("get", "/sync/key/views").then(setViews).catch(() => {}); });
  const copy = () => copyText(shown.token);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const check = (t) => run(async () => setTest(await api("post", "/sync/key/test", { token: t })));
  const save = () => run(async () => { setD(await api("put", "/sync/key", { token })); setToken(""); setTest(null); setShown(null); toast.success("Nouvelle clé de synchronisation enregistrée"); });
  const reset = () => run(async () => { setD(await api("delete", "/sync/key")); setTest(null); setShown(null); toast.success("Clé d'origine rétablie"); });
  if (!d) return <p className="text-sm text-stone-500">Chargement…</p>;
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="sync-key-panel">
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4 text-sm">
        <h3 className="font-heading text-base font-bold text-stone-900">Clé utilisée actuellement</h3>
        <code onClick={() => shown && copy()} title={shown ? "Cliquer pour copier" : undefined} className={`block rounded-md bg-stone-100 px-3 py-2 font-mono break-all select-all ${shown ? "cursor-copy" : ""}`} data-testid="sync-key-masked">{shown ? shown.token : d.masked}</code>
        <div className="flex flex-wrap items-center gap-2">
          {shown ? (
            <>
              <Button variant="outline" onClick={() => setShown(null)} data-testid="sync-key-hide"><EyeOff className="h-4 w-4 mr-1.5" /> Masquer</Button>
              <Button variant="outline" onClick={copy} data-testid="sync-key-copy"><Copy className="h-4 w-4 mr-1.5" /> Copier</Button>
              <span className="text-xs text-stone-500" data-testid="sync-key-countdown">Masquage dans {shown.left} s</span>
            </>
          ) : <Button variant="outline" disabled={busy} onClick={reveal} data-testid="sync-key-reveal"><Eye className="h-4 w-4 mr-1.5" /> Afficher</Button>}
        </div>
        <p className="text-stone-600">{d.source === "custom" ? `Clé remplacée le ${fmtDate(d.updated_at?.slice(0, 16))} par ${d.updated_by}` : "Clé d'origine de la plateforme"}</p>
        <p className="text-xs text-stone-500 break-all">Serveur Sehatic-Pedia : {d.url}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy} onClick={() => check("")} data-testid="sync-key-test-current">Tester la connexion</Button>
          {d.source === "custom" && <Button variant="outline" disabled={busy} onClick={reset} data-testid="sync-key-reset">Rétablir la clé d'origine</Button>}
        </div>
        {test && <p className={`flex items-center gap-1.5 font-medium ${test.ok ? "text-green-700" : "text-red-700"}`} data-testid="sync-key-test-result">{test.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{test.detail}</p>}
        <div className="pt-2 border-t border-stone-100" data-testid="sync-key-views">
          <p className="text-xs uppercase tracking-[0.15em] text-stone-500">Derniers affichages</p>
          {views.length === 0 ? <p className="text-stone-500 mt-1">Jamais affichée.</p> : views.map((v, i) => <p key={i} className="text-stone-600 mt-1">{fmtDate(v.at?.slice(0, 16))} · {v.by}{v.ip ? ` · ${v.ip}` : ""}</p>)}
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4 text-sm">
        <h3 className="font-heading text-base font-bold text-stone-900">Remplacer la clé</h3>
        <p className="text-stone-600">Collez la nouvelle clé fournie par Sehatic-Pedia. Elle est testée avant d'être enregistrée : une clé refusée n'interrompt jamais la synchronisation.</p>
        <Input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Nouvelle clé de synchronisation" data-testid="sync-key-input" />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy || token.length < 8} onClick={() => check(token)} data-testid="sync-key-test-new">Tester</Button>
          <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={busy || token.length < 8} onClick={save} data-testid="sync-key-save">Tester et enregistrer</Button>
        </div>
      </div>
    </div>
  );
};
